import { useState } from 'react';
import { 
  MousePointer2,
  Square,
  Circle,
  Type,
  Image,
  Table2,
  FormInput,
  ChevronDown,
  ToggleLeft,
  CheckSquare,
  CircleDot,
  LayoutGrid,
  Layers,
  CreditCard,
  PanelTop,
  ListOrdered,
  ChevronRight,
  Star,
  Hand,
  X,
  Smartphone,
  Tablet,
  Monitor,
  ArrowRight,
  Frame,
} from 'lucide-react';
import type { ElementType, DeviceType } from '../../types/whiteboard';

export interface SelectedElement {
  id: string;
  type: ElementType;
  label: string;
}

interface Props {
  activeTool: string | null;
  onToolChange: (tool: string) => void;
  selectedElements?: SelectedElement[];
  onRemoveElement?: (elementId: string) => void;
  onElementClick?: (elementId: string) => void;
}

interface ToolItem {
  type: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  shortcut?: string;
  category: string;
}

const tools: ToolItem[] = [
  { type: 'select', icon: MousePointer2, label: 'Select', shortcut: 'V', category: 'tools' },
  { type: 'pan', icon: Hand, label: 'Pan', shortcut: 'H', category: 'tools' },
  { type: 'button', icon: Square, label: 'Button', shortcut: 'B', category: 'interactive' },
  { type: 'input', icon: FormInput, label: 'Input', shortcut: 'I', category: 'interactive' },
  { type: 'dropdown', icon: ChevronDown, label: 'Dropdown', shortcut: 'D', category: 'interactive' },
  { type: 'checkbox', icon: CheckSquare, label: 'Checkbox', category: 'interactive' },
  { type: 'radio_group', icon: CircleDot, label: 'Radio', category: 'interactive' },
  { type: 'toggle', icon: ToggleLeft, label: 'Toggle', category: 'interactive' },
  { type: 'table', icon: Table2, label: 'Table', shortcut: 'T', category: 'data' },
  { type: 'list', icon: ListOrdered, label: 'List', category: 'data' },
  { type: 'text', icon: Type, label: 'Text', shortcut: 'X', category: 'content' },
  { type: 'image', icon: Image, label: 'Image', category: 'content' },
  { type: 'icon', icon: Star, label: 'Icon', category: 'content' },
  { type: 'shape_rect', icon: Square, label: 'Rectangle', shortcut: 'R', category: 'shapes' },
  { type: 'shape_circle', icon: Circle, label: 'Circle', shortcut: 'O', category: 'shapes' },
  { type: 'container', icon: LayoutGrid, label: 'Container', category: 'layout' },
  { type: 'card', icon: CreditCard, label: 'Card', category: 'layout' },
  { type: 'modal', icon: Layers, label: 'Modal', category: 'layout' },
  { type: 'tabs', icon: PanelTop, label: 'Tabs', category: 'layout' },
  { type: 'accordion', icon: ChevronRight, label: 'Accordion', category: 'layout' },
];

const categories = [
  { id: 'tools', label: 'Tools' },
  { id: 'frames', label: 'Frames' },
  { id: 'arrows', label: 'Connect' },
  { id: 'interactive', label: 'Interactive' },
  { id: 'data', label: 'Data' },
  { id: 'content', label: 'Content' },
  { id: 'shapes', label: 'Shapes' },
  { id: 'layout', label: 'Layout' },
];

const deviceFrameOptions: { type: DeviceType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { type: 'iphone_15', label: 'iPhone 15', icon: Smartphone },
  { type: 'iphone_se', label: 'iPhone SE', icon: Smartphone },
  { type: 'android', label: 'Android', icon: Smartphone },
  { type: 'ipad', label: 'iPad', icon: Tablet },
  { type: 'desktop', label: 'Desktop', icon: Monitor },
  { type: 'desktop_wide', label: 'Wide', icon: Monitor },
];

export function WhiteboardToolbar({ 
  activeTool, 
  onToolChange, 
  selectedElements = [], 
  onRemoveElement,
  onElementClick 
}: Props) {
  const [showFrameMenu, setShowFrameMenu] = useState(false);

  const handleClick = (tool: ToolItem) => {
    onToolChange(tool.type);
  };

  const getElementIcon = (type: ElementType) => {
    const tool = tools.find(t => t.type === type);
    return tool?.icon || Square;
  };

  const isFrameToolActive = activeTool?.startsWith('frame:');

  return (
    <aside className="w-16 bg-surface border-r border-default flex flex-col items-center py-3 overflow-y-auto scrollbar-thin">
      {categories.map((category, categoryIndex) => {
        // ====== Frames section ======
        if (category.id === 'frames') {
          return (
            <div key={category.id} className="w-full">
              <div className="w-10 h-px bg-border mx-auto my-2" />
              <div className="flex flex-col items-center gap-1 px-2">
                {/* Frame tool with dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowFrameMenu(!showFrameMenu)}
                    className={`
                      w-12 h-12 flex flex-col items-center justify-center rounded-lg
                      transition-all duration-150 group relative
                      ${isFrameToolActive
                        ? 'bg-primary text-white shadow-md'
                        : 'text-muted hover:bg-surface-2 hover:text-default'
                      }
                    `}
                    title="Add Frame (F)"
                  >
                    <Frame className="w-5 h-5" />
                    <span className="text-[9px] mt-0.5 font-medium">Frame</span>
                    <ChevronDown className="w-2.5 h-2.5 absolute bottom-1.5 right-1.5 opacity-60" />
                  </button>

                  {showFrameMenu && (
                    <div className="absolute left-full ml-2 top-0 bg-surface border border-default rounded-lg shadow-lg py-1 z-50 min-w-[150px]">
                      {deviceFrameOptions.map(opt => {
                        const DevIcon = opt.icon;
                        return (
                          <button
                            key={opt.type}
                            onClick={() => {
                              onToolChange(`frame:${opt.type}`);
                              setShowFrameMenu(false);
                            }}
                            className={`
                              w-full text-left px-3 py-2 text-xs hover:bg-surface-2 transition-colors
                              flex items-center gap-2
                              ${activeTool === `frame:${opt.type}` ? 'text-primary font-medium bg-primary-dim' : 'text-default'}
                            `}
                          >
                            <DevIcon className="w-3.5 h-3.5" />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        // ====== Arrow section ======
        if (category.id === 'arrows') {
          return (
            <div key={category.id} className="w-full">
              <div className="w-10 h-px bg-border mx-auto my-2" />
              <div className="flex flex-col items-center gap-1 px-2">
                <button
                  onClick={() => onToolChange('arrow')}
                  className={`
                    w-12 h-12 flex flex-col items-center justify-center rounded-lg
                    transition-all duration-150 group relative
                    ${activeTool === 'arrow'
                      ? 'bg-primary text-white shadow-md'
                      : 'text-muted hover:bg-surface-2 hover:text-default'
                    }
                  `}
                  title="Arrow (A)"
                >
                  <ArrowRight className="w-5 h-5" />
                  <span className="text-[9px] mt-0.5 font-medium">Arrow</span>
                </button>
              </div>
            </div>
          );
        }

        // ====== Standard categories ======
        const categoryTools = tools.filter(t => t.category === category.id);
        if (categoryTools.length === 0) return null;
        
        return (
          <div key={category.id} className="w-full">
            {categoryIndex > 0 && (
              <div className="w-10 h-px bg-border mx-auto my-2" />
            )}
            
            <div className="flex flex-col items-center gap-1 px-2">
              {categoryTools.map((tool) => {
                const Icon = tool.icon;
                const isActive = activeTool === tool.type;
                
                return (
                  <button
                    key={tool.type}
                    onClick={() => handleClick(tool)}
                    className={`
                      w-12 h-12 flex flex-col items-center justify-center rounded-lg
                      transition-all duration-150 group relative
                      ${isActive 
                        ? 'bg-primary text-white shadow-md' 
                        : 'text-muted hover:bg-surface-2 hover:text-default'
                      }
                    `}
                    title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[9px] mt-0.5 font-medium truncate max-w-full">
                      {tool.label}
                    </span>
                    
                    {/* Tooltip */}
                    <span className="
                      absolute left-full ml-3 px-2.5 py-1.5 
                      text-xs font-medium bg-surface border border-default 
                      rounded-md shadow-lg opacity-0 group-hover:opacity-100 
                      pointer-events-none transition-opacity whitespace-nowrap z-50
                      flex items-center gap-2
                    ">
                      {tool.label}
                      {tool.shortcut && (
                        <kbd className="px-1.5 py-0.5 text-[10px] bg-surface-2 rounded text-muted">
                          {tool.shortcut}
                        </kbd>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Added Elements Section */}
      {selectedElements.length > 0 && (
        <div className="w-full mt-auto pt-2">
          <div className="w-10 h-px bg-border mx-auto mb-2" />
          
          <div className="px-2">
            <p className="text-[9px] text-muted text-center mb-1 font-medium">
              Added ({selectedElements.length})
            </p>
            <div className="flex flex-col gap-1">
              {selectedElements.map((element) => {
                const Icon = getElementIcon(element.type);
                
                return (
                  <div
                    key={element.id}
                    onClick={() => onElementClick?.(element.id)}
                    className={`
                      w-full flex items-center gap-1 px-1.5 py-1 rounded
                      bg-surface-2 border border-default text-[10px] group
                      ${onElementClick ? 'cursor-pointer hover:bg-primary/10 hover:border-primary/30' : ''}
                    `}
                    title={`Click to highlight ${element.label}`}
                  >
                    <Icon className="w-3 h-3 text-muted flex-shrink-0" />
                    <span className="flex-1 truncate text-default">
                      {element.label}
                    </span>
                    {onRemoveElement && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveElement(element.id);
                        }}
                        className="p-0.5 text-muted hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Remove element"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
